import React, { useState, useEffect, useContext } from "react";
import "./logs.scss";
import Sidebar from "../../components/sidebar/Sidebar";
import Navbar from "../../components/navbar/Navbar";
import { useAuth } from "../../context/AuthContext";
import { 
  subscribeToLogs, 
  getLogs, 
  LOG_TYPES, 
  LOG_PRIORITIES 
} from "../../firebase/logService";
import { formatDistanceToNow } from "date-fns";

const Logs = () => {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterType, setFilterType] = useState("");
  const [filterPriority, setFilterPriority] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedLog, setSelectedLog] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  // Get priority color
  const getPriorityColor = (priority) => {
    switch (priority) {
      case LOG_PRIORITIES.CRITICAL:
        return "#ff4757";
      case LOG_PRIORITIES.HIGH:
        return "#ffa502";
      case LOG_PRIORITIES.MEDIUM:
        return "#3742fa";
      case LOG_PRIORITIES.LOW:
        return "#2ed573";
      default:
        return "#747d8c";
    }
  };

  // Get type icon
  const getTypeIcon = (type) => {
    switch (type) {
      case LOG_TYPES.USER_CREATED:
      case LOG_TYPES.USER_UPDATED:
      case LOG_TYPES.USER_DELETED:
      case LOG_TYPES.USER_LOGIN:
      case LOG_TYPES.USER_LOGOUT:
        return "👤";
      case LOG_TYPES.APARTMENT_CREATED:
      case LOG_TYPES.APARTMENT_UPDATED:
      case LOG_TYPES.APARTMENT_DELETED:
      case LOG_TYPES.APARTMENT_OCCUPIED:
      case LOG_TYPES.APARTMENT_VACATED:
        return "🏠";
      case LOG_TYPES.PAYMENT_CREATED:
      case LOG_TYPES.PAYMENT_UPDATED:
      case LOG_TYPES.PAYMENT_DELETED:
      case LOG_TYPES.PAYMENT_STATUS_CHANGED:
      case LOG_TYPES.PAYMENT_OVERDUE:
      case LOG_TYPES.LATE_FEE_APPLIED:
        return "💰";
      case LOG_TYPES.MAINTENANCE_CREATED:
      case LOG_TYPES.MAINTENANCE_UPDATED:
      case LOG_TYPES.MAINTENANCE_DELETED:
      case LOG_TYPES.MAINTENANCE_STATUS_CHANGED:
      case LOG_TYPES.MAINTENANCE_COMPLETED:
        return "🔧";
      case LOG_TYPES.NOTICE_CREATED:
      case LOG_TYPES.NOTICE_UPDATED:
      case LOG_TYPES.NOTICE_DELETED:
        return "📢";
      case LOG_TYPES.MESSAGE_SENT:
      case LOG_TYPES.MESSAGE_DELETED:
        return "💬";
      case LOG_TYPES.NOTIFICATION_CREATED:
      case LOG_TYPES.NOTIFICATION_READ:
        return "🔔";
      case LOG_TYPES.BACKUP_CREATED:
      case LOG_TYPES.BACKUP_DELETED:
      case LOG_TYPES.RESTORE_PERFORMED:
      case LOG_TYPES.DATA_CLEARED:
        return "💾";
      case LOG_TYPES.SYSTEM_HEALTH_CHECK:
      case LOG_TYPES.SYSTEM_ERROR:
      case LOG_TYPES.SYSTEM_WARNING:
        return "⚙️";
      default:
        return "📝";
    }
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "Unknown";
    
    try {
      const date = timestamp.seconds 
        ? new Date(timestamp.seconds * 1000)
        : new Date(timestamp);
      return formatDistanceToNow(date, { addSuffix: true });
    } catch (error) {
      return "Invalid date";
    }
  };

  // Filter logs
  const filteredLogs = logs.filter(log => {
    const matchesType = !filterType || log.type === filterType;
    const matchesPriority = !filterPriority || log.priority === filterPriority;
    const matchesSearch = !searchTerm || 
      log.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (log.data?.tenantName && log.data.tenantName.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (log.data?.userEmail && log.data.userEmail.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return matchesType && matchesPriority && matchesSearch;
  });

  // Load initial logs
  useEffect(() => {
    const loadLogs = async () => {
      try {
        setLoading(true);
        const result = await getLogs({ limit: 100 });
        if (result.success) {
          setLogs(result.logs);
        } else {
          setError(result.error);
        }
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    loadLogs();
  }, []);

  // Subscribe to real-time logs
  useEffect(() => {
    const unsubscribe = subscribeToLogs((newLogs) => {
      setLogs(newLogs);
    }, { limit: 100 });

    return () => unsubscribe();
  }, []);

  // Handle log selection
  const handleLogClick = (log) => {
    setSelectedLog(log);
    setShowDetails(true);
  };

  // Close details modal
  const closeDetails = () => {
    setShowDetails(false);
    setSelectedLog(null);
  };

  // Clear filters
  const clearFilters = () => {
    setFilterType("");
    setFilterPriority("");
    setSearchTerm("");
  };

  // Get log type display name
  const getTypeDisplayName = (type) => {
    return type.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  return (
    <div className="logs">
      <Sidebar />
      <div className="logsContainer">
        <Navbar />
        
        <div className="logsContent">
          <div className="logsHeader">
            <h1>System Logs</h1>
            <p>Real-time system activity and events</p>
          </div>

          {/* Filters */}
          <div className="logsFilters">
            <div className="filterGroup">
              <input
                type="text"
                placeholder="Search logs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="searchInput"
              />
            </div>

            <div className="filterGroup">
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="filterSelect"
              >
                <option value="">All Types</option>
                <option value={LOG_TYPES.USER_CREATED}>User Created</option>
                <option value={LOG_TYPES.USER_UPDATED}>User Updated</option>
                <option value={LOG_TYPES.USER_DELETED}>User Deleted</option>
                <option value={LOG_TYPES.PAYMENT_CREATED}>Payment Created</option>
                <option value={LOG_TYPES.PAYMENT_UPDATED}>Payment Updated</option>
                <option value={LOG_TYPES.PAYMENT_STATUS_CHANGED}>Payment Status Changed</option>
                <option value={LOG_TYPES.MAINTENANCE_CREATED}>Maintenance Created</option>
                <option value={LOG_TYPES.MAINTENANCE_UPDATED}>Maintenance Updated</option>
                <option value={LOG_TYPES.MAINTENANCE_COMPLETED}>Maintenance Completed</option>
                <option value={LOG_TYPES.BACKUP_CREATED}>Backup Created</option>
                <option value={LOG_TYPES.RESTORE_PERFORMED}>Restore Performed</option>
                <option value={LOG_TYPES.DATA_CLEARED}>Data Cleared</option>
                <option value={LOG_TYPES.SYSTEM_ERROR}>System Error</option>
                <option value={LOG_TYPES.SYSTEM_WARNING}>System Warning</option>
              </select>
            </div>

            <div className="filterGroup">
              <select
                value={filterPriority}
                onChange={(e) => setFilterPriority(e.target.value)}
                className="filterSelect"
              >
                <option value="">All Priorities</option>
                <option value={LOG_PRIORITIES.CRITICAL}>Critical</option>
                <option value={LOG_PRIORITIES.HIGH}>High</option>
                <option value={LOG_PRIORITIES.MEDIUM}>Medium</option>
                <option value={LOG_PRIORITIES.LOW}>Low</option>
              </select>
            </div>

            <button onClick={clearFilters} className="clearFiltersBtn">
              Clear Filters
            </button>
          </div>

          {/* Logs List */}
          <div className="logsList">
            {loading ? (
              <div className="loadingState">
                <div className="spinner"></div>
                <p>Loading logs...</p>
              </div>
            ) : error ? (
              <div className="errorState">
                <p>Error loading logs: {error}</p>
              </div>
            ) : filteredLogs.length === 0 ? (
              <div className="emptyState">
                <p>No logs found</p>
              </div>
            ) : (
              filteredLogs.map((log) => (
                <div
                  key={log.id}
                  className="logItem"
                  onClick={() => handleLogClick(log)}
                >
                  <div className="logIcon">
                    <span>{getTypeIcon(log.type)}</span>
                  </div>
                  
                  <div className="logContent">
                    <div className="logHeader">
                      <h3>{log.message}</h3>
                      <span 
                        className="priorityBadge"
                        style={{ backgroundColor: getPriorityColor(log.priority) }}
                      >
                        {log.priority}
                      </span>
                    </div>
                    
                    <div className="logMeta">
                      <span className="logType">{getTypeDisplayName(log.type)}</span>
                      <span className="logTime">{formatTimestamp(log.timestamp)}</span>
                      {log.performedBy && (
                        <span className="performedBy">by {log.performedBy}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Log Details Modal */}
        {showDetails && selectedLog && (
          <div className="logDetailsModal" onClick={closeDetails}>
            <div className="modalContent" onClick={(e) => e.stopPropagation()}>
              <div className="modalHeader">
                <h2>Log Details</h2>
                <button onClick={closeDetails} className="closeBtn">×</button>
              </div>
              
              <div className="modalBody">
                <div className="detailRow">
                  <label>Message:</label>
                  <p>{selectedLog.message}</p>
                </div>
                
                <div className="detailRow">
                  <label>Type:</label>
                  <p>{getTypeDisplayName(selectedLog.type)}</p>
                </div>
                
                <div className="detailRow">
                  <label>Priority:</label>
                  <span 
                    className="priorityBadge"
                    style={{ backgroundColor: getPriorityColor(selectedLog.priority) }}
                  >
                    {selectedLog.priority}
                  </span>
                </div>
                
                <div className="detailRow">
                  <label>Timestamp:</label>
                  <p>{selectedLog.timestamp?.seconds 
                    ? new Date(selectedLog.timestamp.seconds * 1000).toLocaleString()
                    : new Date(selectedLog.timestamp).toLocaleString()
                  }</p>
                </div>
                
                {selectedLog.performedBy && (
                  <div className="detailRow">
                    <label>Performed By:</label>
                    <p>{selectedLog.performedBy}</p>
                  </div>
                )}
                
                {selectedLog.data && Object.keys(selectedLog.data).length > 0 && (
                  <div className="detailRow">
                    <label>Additional Data:</label>
                    <pre className="dataJson">
                      {JSON.stringify(selectedLog.data, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Logs; 